'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GeoPointType, ProductOfCategory, ProductType } from '@/@types';
import { Button, Icon } from '@/components/core';

import { Trans } from '@lingui/react/macro';
import clsx from 'clsx';
import configuration from '@/configuration';
import { isProductOfType } from '@/services/api/parsers/utils';
import * as styles from './map.styles';
import PlaceDetails from './_components/place-details';

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    L: any;
  }
}

type MapOptionsType = { center?: GeoPointType; delta: number };

const PeoopleMap: React.FC<
  React.PropsWithChildren & {
    disableDetails?: boolean;
    /**
     * @deprecated use zoom instead
     */
    delta?: number;
    markers: ProductOfCategory<'restaurants' | 'places'>[];
    // eslint-disable-next-line no-unused-vars
    onChange?: (options: MapOptionsType) => any;
    // eslint-disable-next-line no-unused-vars
    onLoad?: (options: MapOptionsType) => any;
    center?: GeoPointType;
    className?: string;
  }
> = ({ markers, onChange, onLoad, children, center, delta = 0.004, disableDetails, className }) => {
  const id = React.useId();
  const mapRef = useRef<any>(null);
  const rootRef = useRef<any>(null);

  const [details, setDetails] = useState<ProductType | undefined>();
  const [showDetails, setShowDetails] = useState<boolean>(false);

  const [mapOptions, setMapOptions] = useState<MapOptionsType>({ delta, center });
  const [currentPosition, setCurrentPosition] = useState<GeoPointType>();

  const [loaded, setLoaded] = useState<'loading' | 'loaded' | 'ready'>('loading');

  // Load map scripts
  useEffect(() => {
    if (window.L) {
      setLoaded('loaded');
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => {
      setLoaded('loaded');
    };

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';

    document.body.appendChild(link);
    document.body.appendChild(script);
  }, []);

  // remove map when component is unmounted
  useEffect(() => {
    return () => {
      mapRef.current?.remove?.();
      mapRef.current = undefined;
    };
  }, []);

  // create map instance and change listener
  useEffect(() => {
    if (loaded !== 'loaded' || mapRef.current) {
      return;
    }
    // Creating a map object
    mapRef.current = new window.L.Map(id, {
      center: mapOptions.center || { lat: 40.416775, lng: -3.70329 },
      zoom: 18,
      zoomControl: false,
    });

    const map = mapRef.current;

    // Creating a Layer object
    // maps http://{s}.google.com/vt?lyrs=m&x={x}&y={y}&z={z}
    // satellite http://{s}.google.com/vt?lyrs=s&x={x}&y={y}&z={z}
    // openstreetmap http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    const layer = new window.L.TileLayer('http://{s}.google.com/vt?lyrs=m&x={x}&y={y}&z={z}', {
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
    });

    // Adding layer to the map
    map.addLayer(layer);
    onLoad?.({
      center: map.getCenter(),
      delta: map.getBounds().getNorth() - map.getBounds().getSouth(),
    });
    setLoaded('ready');
  }, [loaded, onLoad, mapOptions, id, onChange]);

  // add listener to map
  useEffect(() => {
    if (loaded !== 'ready') {
      return undefined;
    }
    const update = (evt) => {
      onChange?.({
        center: evt.target.getCenter(),
        delta: evt.target.getBounds().getNorth() - evt.target.getBounds().getSouth(),
      });
    };
    const map = mapRef.current;
    map.addEventListener('moveend', update);
    map.addEventListener('zoom', update);
    return () => {
      map.removeEventListener('moveend', update);
      map.removeEventListener('zoom', update);
    };
  }, [loaded, onChange]);

  // Add markers to the map
  useEffect(() => {
    if (loaded !== 'ready') {
      return undefined;
    }
    const map = mapRef.current;
    const markerRefs = markers
      .filter((item) => {
        return isProductOfType(item, ['restaurants', 'places']);
      })
      .filter((item) => {
        return !!item.extraInfo?.location?.geo || item.geo;
      })
      .map((item) => {
        const geo = item.extraInfo?.location?.geo || item.geo;
        if (!geo) {
          return null;
        }
        const icon = new window.L.Icon({
          iconUrl: `${configuration.host}/markers/${item.oldSuncategory || '72'}.png`,
        });
        const marker = new window.L.Marker([geo.lat, geo.lng], { icon }).addTo(map);
        marker.addEventListener('click', () => {
          setDetails(item);
        });
        return marker;
      });
    return () => {
      markerRefs.forEach((marker) => {
        map.removeLayer(marker);
      });
    };
  }, [loaded, markers]);

  useEffect(() => {
    if (!mapOptions.center || !mapRef.current) {
      return;
    }
    mapRef.current.flyTo(mapOptions.center);
  }, [mapOptions.center]);

  const centerMap = useCallback((point) => {
    setMapOptions((current) => {
      return {
        ...current,
        center: point,
      };
    });
  }, []);

  const centerMe = useCallback(() => {
    navigator.geolocation.getCurrentPosition(
      async (location) => {
        setCurrentPosition({
          lat: location.coords.latitude,
          lng: location.coords.longitude,
        });
        centerMap({
          lat: location.coords.latitude,
          lng: location.coords.longitude,
        });
      },
      (error) => {
        console.log('on error', error);
      },
    );
  }, [centerMap]);

  useEffect(() => {
    if (!center) {
      const obsever = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              centerMe();
              obsever.disconnect();
            }
          });
        },
        { threshold: 0.5 },
      );
      obsever.observe(rootRef.current);
      return;
    }
    centerMap(center);
  }, [centerMap, center, centerMe]);

  useEffect(() => {
    if (details) {
      setShowDetails(true);
    }
  }, [details]);

  useEffect(() => {
    if (!showDetails) {
      setTimeout(() => {
        setDetails(undefined);
      }, 300);
    }
  }, [showDetails]);

  return (
    <div ref={rootRef} className={clsx(className, styles.root)}>
      <div className={styles.map} id={id} />
      {loaded !== 'ready' ? (
        <div className={clsx(className, styles.loader)}>
          <video width={150} height={150} controls={false} autoPlay muted loop playsInline>
            <source src={`${configuration.host}/video/map-loader.webm`} type="video/webm" />
            <source src={`${configuration.host}/video/map-loader.gif`} type="video/gif" />
          </video>
          <span>
            <Trans>Loading map</Trans>
          </span>
        </div>
      ) : (
        <>
          <Button variant="secondary" onClick={centerMe} className={styles.findMe}>
            <Icon name="locate" />
          </Button>
          {children}
          {!disableDetails && (
            <PlaceDetails
              onClose={() => {
                setShowDetails(false);
              }}
              className={clsx(styles.placeDetails, { [styles.show]: showDetails })}
              place={details}
              currentPosition={currentPosition}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PeoopleMap;
